import React from 'react'

import { Button, Icon, type PageHeaderProps, Popover } from '../../../module'
import styles from './_table-header.module.scss'
import CustomizeTableColumns, {
  type CustomizeTableColumnsProps,
} from '../CustomizeTableColumns/CustomizeTableColumns'
import {
  type ActiveTableFilterObj,
  type ActiveTableFilterProps,
} from '../TableFilter/TableFilter'
import ScrollingContainer from '../../ScrollingContainer/ScrollingContainer'
import { PageHeader } from '../../PageHeader/PageHeader'
import { c } from '../../../translations/LibraryTranslationService'

type ColumnFilterProps = {
  activeFilters: ActiveTableFilterProps
  remove: (filter?: ActiveTableFilterObj) => void
}

export type TableHeaderProps<OptionType> = PageHeaderProps<OptionType> & {
  /** Optional column filter available */
  columnFilterProps?: ColumnFilterProps
  /** Optionally use the "Customize Table Column" experience where we can hide and show columns. This also allows us to add other form elements above and below the custom column `MultiSelect` component. */
  customColumnProps?: CustomizeTableColumnsProps
  /** Optional prop to add a test id to the TableHeader for QA testing */
  qaTestId?: string
}

const TableHeader = <OptionType,>(
  props: TableHeaderProps<OptionType>,
): React.JSX.Element => {
  const {
    columnFilterProps,
    customColumnProps,
    qaTestId = 'table-header',
  } = props
  return (
    <PageHeader
      {...props}
      bottomSectionChildren={
        <>
          {
            // Selected column table filters
            columnFilterProps?.activeFilters ? (
              <div className={styles.filterContainer}>
                <Icon icon='filter' color='purple' />
                {Object.keys(columnFilterProps.activeFilters).length > 0 ? (
                  <div className={styles.filterSection}>
                    {Object.keys(columnFilterProps.activeFilters).map((af) => (
                      <span key={af}>
                        {Array.isArray(
                          columnFilterProps.activeFilters[af].value,
                        ) ? (
                          <Popover
                            maxWidth='250px'
                            popoverContent={
                              <ScrollingContainer height='auto' hideGradients>
                                <ul className={styles.pillTooltip}>
                                  {(
                                    columnFilterProps.activeFilters[af]
                                      .value as (number | string)[]
                                  )?.map((item, index) => (
                                    <li key={index}>{item}</li>
                                  ))}
                                </ul>
                              </ScrollingContainer>
                            }
                          >
                            {({ setVisible }) => {
                              return (
                                <FilterPill
                                  filter={columnFilterProps.activeFilters[af]}
                                  onClick={columnFilterProps.remove}
                                  setVisible={setVisible}
                                />
                              )
                            }}
                          </Popover>
                        ) : (
                          <FilterPill
                            filter={columnFilterProps.activeFilters[af]}
                            onClick={columnFilterProps.remove}
                          />
                        )}
                      </span>
                    ))}
                    <Button
                      styleType='text-blue'
                      onClick={() => columnFilterProps.remove()}
                    >
                      {c('clearAll')}
                    </Button>
                  </div>
                ) : (
                  <span className={styles.noFilter}>
                    {c('noFiltersApplied')}
                  </span>
                )}
              </div>
            ) : null
          }
          {
            // Optional bottomSectionChildren
            props?.bottomSectionChildren ? props?.bottomSectionChildren : null
          }
        </>
      }
      rightSectionChildren={
        customColumnProps || props?.rightSectionChildren ? (
          <>
            {/** Optional customColumnProps & rightSectionChildren */}
            {customColumnProps ? (
              <CustomizeTableColumns {...customColumnProps} />
            ) : null}
            {props?.rightSectionChildren ? props?.rightSectionChildren : null}
          </>
        ) : null
      }
      qaTestId={qaTestId}
    />
  )
}

export default TableHeader

type FilterPillProps = {
  filter: ActiveTableFilterObj
  onClick: (filter: ActiveTableFilterObj) => void
  setVisible?: (visible: boolean) => void
}

const FilterPill = ({ filter, onClick, setVisible }: FilterPillProps) => {
  return (
    <div className={styles.filterPill}>
      {setVisible ? (
        <Button as='unstyled' onClick={() => setVisible(true)}>
          <span>{filter.label}</span>
        </Button>
      ) : (
        <span>{filter.label}</span>
      )}
      <Button as='unstyled' onClick={() => onClick(filter)}>
        <Icon icon='x' iconSize='8px' />
      </Button>
    </div>
  )
}
